import { LinkProps, Outlet } from "@tanstack/react-router";
import { FC, ReactNode, Suspense, useState } from "react";
import { RecordScreenContentTabs } from "@/lib/Components/Screens/RecordScreen/Components/RecordScreenContentTabs";
import {
  Action,
  ActionGroupButton,
  ActionGroupButtons,
} from "@/lib/Components/Actions/ActionGroupButtons";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { cn } from "@/lib/utils";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { Button } from "@/components/catalyst/button";
import { Heading } from "@/components/catalyst/heading";
import { CopyToClipboard } from "@/lib/Components/Common/CopyToClipboard";
import { Badge } from "@/components/catalyst/badge";

export type RecordScreenLink = {
  name: string;
  Icon: FC<any>;
  notificationCount?: number;
  isHidden?: boolean;
  isAdminTab?: boolean;
  isBeta?: boolean;
  fuzzy?: boolean;
} & LinkProps;

type RecordScreenProps = {
  title: string | ReactNode;
  banner?: ReactNode;
  tabs?: RecordScreenLink[];
  tabParams?: Record<string, string>;
  actionsGroups?: ActionGroupButton[];
  badges?: ReactNode;
  className?: string;
  breadcrumbs?: ReactNode;
  image?: ReactNode;
};

export function RecordScreen({
  tabs = [],
  tabParams,
  actionsGroups = [],
  banner,
  title,
  badges,
  className,
  breadcrumbs,
  image,
}: RecordScreenProps) {
  return (
    <>
      <RecordScreenHeader actionGroups={actionsGroups} />
      <div
        className={cn("h-full px-3 sm:px-6 lg:overflow-y-scroll", className)}
      >
        <div className="mx-auto w-full max-w-6xl pb-24 pt-4">
          <header className="space-y-4">
            {breadcrumbs}
            <div className="space-y-6 xl:flex xl:items-center xl:gap-4 xl:space-y-0">
              {image}
              <div className="grow">
                <Heading>
                  {typeof title === "string" ? (
                    <CopyToClipboard text={title} />
                  ) : (
                    title
                  )}
                </Heading>
                {badges ? (
                  <p className="mt-1 flex flex-wrap gap-2">{badges}</p>
                ) : null}
              </div>
              <ActionGroupButtons buttons={actionsGroups} />
            </div>
            <RecordScreenContentTabs tabs={tabs} params={tabParams} />
          </header>
          {banner}
          <Suspense
            fallback={
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </div>
      </div>
    </>
  );
}

function RecordScreenHeader({
  actionGroups,
}: {
  actionGroups: ActionGroupButton[];
}) {
  const { isAdmin } = useTenant();

  const groups = actionGroups.flatMap((group) => {
    return group.items.map((item) =>
      item.actions.filter((action) => {
        const isHiddenBecauseNotAdmin = !!action.isAdminAction && !isAdmin;

        return (
          !action.isHidden && !action.isHiddenTopBar && !isHiddenBecauseNotAdmin
        );
      }),
    );
  });

  //Add some right padding to account for the missing scrollbar
  return (
    <nav className="scroll-gutter-stable hidden h-[64px] overflow-y-auto border-b border-gray-200 bg-gray-50 px-6 lg:flex">
      <div className="mx-auto flex w-full max-w-6xl flex-col justify-center">
        <div className="shadow-xs isolate inline-flex rounded-md py-3 sm:space-x-3 sm:shadow-none">
          {groups.map((value, index) => (
            <ActionGroup group={value} key={index} />
          ))}
        </div>
      </div>
    </nav>
  );
}

function ActionGroup({ group }: { group: Action[] }) {
  if (group.length === 0) return null;

  if (group.length === 1) {
    return <ActionButton action={group[0]} />;
  }

  return (
    <span className="isolate inline-flex gap-1">
      {group.map((action, idx) => {
        return <ActionButton key={idx} action={action} />;
      })}
    </span>
  );
}

function ActionButton({ action }: { action: Action }) {
  const [isLoading, setIsLoading] = useState(false);

  const buttonContent = (
    <>
      {action.label}
      {action.isAdminAction ? (
        <Badge className="bg-sky-200" color="sky">
          admin
        </Badge>
      ) : null}
    </>
  );

  const handleClick = async () => {
    setIsLoading(true);
    try {
      await action.handleAction();
    } finally {
      setIsLoading(false);
    }
  };

  if (action.color) {
    return (
      <Button
        color={action.color}
        LeadingIcon={action.Icon}
        isLoading={isLoading}
        onClick={handleClick}
      >
        {buttonContent}
      </Button>
    );
  }

  return (
    <Button
      color="white"
      LeadingIcon={action.Icon}
      isLoading={isLoading}
      onClick={handleClick}
    >
      {buttonContent}
    </Button>
  );
}
